import {
  format,
  formatDistanceStrict,
  fromUnixTime,
  getUnixTime,
  isValid,
  parseISO,
} from 'date-fns';
import { Liquid } from 'liquidjs';
import { PAGED_RENDERER_SCRIPT } from '@realadvisor/paged-renderer/script';
// here we import commonjs for docs
import de from 'date-fns/locale/de/index.js';
import en from 'date-fns/locale/en-GB/index.js';
import es from 'date-fns/locale/es/index.js';
import fr from 'date-fns/locale/fr/index.js';
import it from 'date-fns/locale/it/index.js';
import pl from 'date-fns/locale/pl/index.js';
import pt from 'date-fns/locale/pt/index.js';

// Hoist dynamic imports to module scope — resolved once, reused across renders
const d3ScaleP = import('d3-scale');
const d3ArrayP = import('d3-array');
const remarkableP = import('remarkable');

const MAPBOX_VERSION = '2.14.1';

// Bounded memoization for expensive chart SVG filters
const chartCache = new Map<string, string>();
const MAX_CHART_CACHE = 200;

const memoFilter = (
  name: string,
  fn: (...args: any[]) => Promise<string>,
): ((...args: any[]) => Promise<string>) => {
  return async (...args: any[]) => {
    const key = name + ':' + JSON.stringify(args);
    const hit = chartCache.get(key);
    if (hit != null) return hit;
    const result = await fn(...args);
    if (chartCache.size >= MAX_CHART_CACHE) {
      const firstKey = chartCache.keys().next().value;
      if (firstKey != null) chartCache.delete(firstKey);
    }
    chartCache.set(key, result);
    return result;
  };
};

type Language = 'en' | 'de' | 'fr' | 'it' | 'es' | 'pl' | 'pt';

type Renderer = {
  render: (params: {
    content: string;
    data: any;
    meta: {
      title?: string;
      language: Language;
      googleMapsChannel?: string;
      googleMapsKey?: string;
    };
  }) => Promise<string>;
};

const DATEFNS_LOCALE_MAP = {
  en,
  fr,
  de,
  it,
  es,
  pl,
  pt,
};

type Options = {
  imageProxyDomain: string;
  mainLocale: string;
  dateFnsLocale?: keyof typeof DATEFNS_LOCALE_MAP | null;
  translations?: Record<string, string>;
  sanitize: (raw: string) => string;
};

type ResultType = {
  name: string;
  min: number;
  value: number;
  max: number;
};

type Address = {
  route: string | null;
  streetNumber: string | null;
  postcode: string | null;
  locality: string | null;
};

export const formatAddress = (address: Address): any => {
  let { streetNumber, route, postcode, locality } = address;

  const addressParts = [
    [route, streetNumber].filter(Boolean).join(' ').trim(),
    [postcode, locality].filter(Boolean).join(' ').trim(),
  ].filter(Boolean) as string[];

  return addressParts.join(', ');
};

export function setupRenderer(options: Options): Renderer {
  const { mainLocale, dateFnsLocale, translations = {}, sanitize } = options;
  const mainLocaleLanguage = mainLocale.split('-')[0] as Language;

  const engine = new Liquid({
    cache: true,
    relativeReference: false,
    fs: {
      readFileSync() {
        throw new Error('File read is not allowed in template-renderer');
      },
      async readFile() {
        throw new Error('File read is not allowed in template-renderer');
      },
      existsSync() {
        throw new Error('Work with files is not allowed in template-renderer');
      },
      exists() {
        throw new Error('Work with files is not allowed in template-renderer');
      },
      contains() {
        throw new Error('Work with files is not allowed in template-renderer');
      },
      resolve() {
        throw new Error('Work with files is not allowed in template-renderer');
      },
    },
  });

  engine.registerTag('t', {
    parse: function (tagToken) {
      const rawArgs = tagToken.args?.trim();
      if (!rawArgs) {
        throw new Error('Tag "t" requires at least a key parameter');
      }

      // First capture quoted string, then optionally capture a number, variable, or object path after a comma
      const match = rawArgs.match(
        /^(['"])([^'"]*?)\1(?:\s*,\s*([a-zA-Z_][a-zA-Z0-9_]*(\.[a-zA-Z_][a-zA-Z0-9_]*)*|\d+))?$/,
      );

      if (!match) {
        throw new Error(
          `Invalid translation tag format: "${rawArgs}". Expected: {% t "key" %} or {% t "key", count %}`,
        );
      }

      this.key = match[2]; // The text inside quotes
      this.count = match[3]; // The number after comma, if any
    },

    render: async function (ctx) {
      if (!this.key) {
        console.warn('Translation key is empty');
        return '[MISSING_KEY]';
      }

      // Clean key safely
      const key = this.key.trim().replace(/['"]/g, '');

      // Handle count for pluralization
      let count = 1;
      try {
        if (this.count) {
          const evalCount = await this.liquid.evalValue(this.count, ctx);
          count = Number(evalCount) || 1;
        }
      } catch (error) {
        console.warn(
          `Error evaluating count for translation key "${key}":`,
          error,
        );
      }

      // Get translation with proper pluralization
      const translationKey = count === 1 ? key : `${key}_plural`;
      // Read from render context first (cached engine), fall back to closure
      const ctxTranslations = ctx.get(['data', '__translations']) as
        | Record<string, string>
        | undefined;
      const activeTranslations = ctxTranslations ?? translations;
      const translation = activeTranslations[translationKey];

      if (translation == null) {
        // Log missing translation for debugging
        console.warn(`Missing translation for key: ${translationKey}`);
        return key; // Fallback to key
      }

      return translation;
    },
  });

  // fix for Chrome Skia bug, which cuts French spaces in numbers
  const fixSpaces = (str: string) => {
    const match = str.match(/^([^0-9-]+)(.*)/);

    // Currency symbol and value
    if (!match?.[1] || !match?.[2]) {
      return str;
    }

    return `${match[1].trim()} ${match[2].trim()}`;
  };

  const getAppraisalData = (tenantSettings: any, appraisal: any) => {
    const result: ResultType[] = [];
    if (tenantSettings.activateModelPropertydata === true) {
      result.push({
        name: 'Property data',
        min: appraisal.propertydata?.min,
        value: appraisal.propertydata?.value,
        max: appraisal.propertydata?.max,
      });
    }
    if (tenantSettings.activateModelPricehubble === true) {
      result.push({
        name: 'PH',
        min: appraisal.pricehubble?.min,
        value: appraisal.pricehubble?.value,
        max: appraisal.pricehubble?.max,
      });
    }
    if (tenantSettings.activateModelRealadvisorListings === true) {
      result.push({
        name: 'RAL',
        min: appraisal.realadvisorListing?.min,
        value: appraisal.realadvisorListing?.value,
        max: appraisal.realadvisorListing?.max,
      });
      result.push({
        name: 'RAN',
        min: appraisal.realadvisorNaiveListings?.min,
        value: appraisal.realadvisorNaiveListings?.value,
        max: appraisal.realadvisorNaiveListings?.max,
      });
      result.push({
        name: 'RAX',
        min: appraisal.realadvisorOmniMeta?.min,
        value: appraisal.realadvisorOmniMeta?.value,
        max: appraisal.realadvisorOmniMeta?.max,
      });
    }
    if (tenantSettings.activateModelRealadvisorTransactions === true) {
      result.push({
        name: 'RAT',
        min: appraisal.realadvisorTransaction?.min,
        value: appraisal.realadvisorTransaction?.value,
        max: appraisal.realadvisorTransaction?.max,
      });
      result.push({
        name: 'RAO',
        min: appraisal.realadvisorOpenDataTransaction?.min,
        value: appraisal.realadvisorOpenDataTransaction?.value,
        max: appraisal.realadvisorOpenDataTransaction?.max,
      });
    }
    if (tenantSettings.activateModelRealadvisorPerceived === true) {
      result.push({
        name: 'RAP',
        min: appraisal.realadvisorPerceived?.min,
        value: appraisal.realadvisorPerceived?.value,
        max: appraisal.realadvisorPerceived?.max,
      });
    }
    if (tenantSettings.activateModelIazi === true) {
      result.push({
        name: 'IAZI',
        min: appraisal.iazi?.min,
        value: appraisal.iazi?.value,
        max: appraisal.iazi?.max,
      });
      result.push({
        name: 'IAZI CV',
        min: appraisal.iaziCv?.min,
        value: appraisal.iaziCv?.value,
        max: appraisal.iaziCv?.max,
      });
    }
    if (tenantSettings.activateModelWup === true) {
      result.push({
        name: 'Wuest',
        min: appraisal.wup?.min,
        value: appraisal.wup?.value,
        max: appraisal.wup?.max,
      });
    }
    return result;
  };

  engine.registerFilter('format_address', address => {
    if (!address) return '';
    return formatAddress(address) ?? '';
  });

  const CURRENCY_SETTINGS = {
    CH: { currency: 'CHF', locale: 'de-CH' },
    FR: { currency: 'EUR', locale: 'fr-FR' },
    ES: { currency: 'EUR', locale: 'es-ES' },
    DE: { currency: 'EUR', locale: 'de-DE' },
    IT: { currency: 'EUR', locale: 'it-IT' },
    PL: { currency: 'PLN', locale: 'pl-PL' },
    PT: { currency: 'EUR', locale: 'pt-PT' },
  } as const;

  type CountryCode = keyof typeof CURRENCY_SETTINGS;

  const getCurrencySettings = (countryCode?: string) => {
    if (!countryCode || !(countryCode in CURRENCY_SETTINGS)) {
      return CURRENCY_SETTINGS.CH;
    }
    return CURRENCY_SETTINGS[countryCode as CountryCode];
  };

  engine.registerFilter('number', (number, ...parameterEntries) => {
    const { locale, countryCode, ...params } =
      Object.fromEntries(parameterEntries);
    const resolvedLocale =
      locale ??
      (countryCode ? getCurrencySettings(countryCode).locale : mainLocale);
    return fixSpaces(
      (number ?? 0).toLocaleString(resolvedLocale, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
        ...params,
      }),
    );
  });

  engine.registerFilter('percent', (number, ...parameterEntries) => {
    const { base, locale, ...params } = Object.fromEntries(parameterEntries);
    return fixSpaces(
      (number / base).toLocaleString(locale ?? mainLocale, {
        style: 'percent',
        ...params,
      }),
    );
  });

  engine.registerFilter('normalize_name', title => {
    const formattedName = title.split('_').join(' ');
    return formattedName[0].toUpperCase() + formattedName.slice(1);
  });

  engine.registerFilter('currency', (number, ...parameterEntries) => {
    // `tenantCode` is the pre-rename name of `countryCode`; most doc_templates
    // rows in prod still pass it, so it must stay accepted.
    const { locale, currency, countryCode, tenantCode, ...params } =
      Object.fromEntries(parameterEntries);

    const settings = getCurrencySettings(countryCode || tenantCode);

    return fixSpaces(
      (number ?? 0).toLocaleString(locale ?? settings.locale, {
        style: 'currency',
        currencyDisplay: 'symbol',
        currency: currency ?? settings.currency,
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
        ...params,
      }),
    );
  });

  /*
  calculates median values...
  of the array of values:
  {{ data.prices | median }}
  OR array of objects
  {{ data.listings | median: key='salePrice' }}
  zeroes or nulls are excluded from calculating
   */
  engine.registerFilter('median', (arr, ...parameterEntries) => {
    const median = (arr: number[]) => {
      if (arr.length >= 1) {
        arr = arr.sort((a, b) => a - b);

        if (arr.length % 2 === 0) {
          // return avg of 2 middle values if even
          const subCount: number = arr.length / 2;
          if (arr[subCount] && arr[subCount - 1]) {
            return (arr[subCount - 1]! + arr[subCount]!) / 2;
          }
        }

        return arr[(arr.length - 1) / 2];
      }

      return 0;
    };

    const { key } = Object.fromEntries(parameterEntries);

    let values = arr;
    if (key != null) {
      values = arr.map((item: any) => item[key]);
    }
    values = values.filter(Boolean).map((value: unknown) => {
      if (typeof value === 'string') {
        const parsed = parseISO(value);
        if (isValid(parsed)) {
          return getUnixTime(parsed);
        }

        return 0;
      }

      return value;
    });

    return median(values);
  });

  engine.registerFilter(
    'date_fns',
    (date, formatString, ...parameterEntries) => {
      const { countryCode } = Object.fromEntries(parameterEntries);

      const locale =
        DATEFNS_LOCALE_MAP[
          countryCode
            ? (countryCode.toLowerCase() as keyof typeof DATEFNS_LOCALE_MAP) ??
              mainLocaleLanguage
            : dateFnsLocale ?? mainLocaleLanguage ?? 'en'
        ];

      // Handle both Date objects and ISO strings
      const parsedDate = date instanceof Date ? date : parseISO(String(date));
      return date ? format(parsedDate, formatString, { locale }) : '';
    },
  );

  engine.registerFilter(
    'format_distance_strict',
    (date, ...parameterEntries) => {
      const { countryCode } = Object.fromEntries(parameterEntries);

      const locale =
        DATEFNS_LOCALE_MAP[
          countryCode
            ? (countryCode.toLowerCase() as keyof typeof DATEFNS_LOCALE_MAP) ??
              mainLocaleLanguage
            : dateFnsLocale ?? mainLocaleLanguage ?? 'en'
        ];

      return date
        ? formatDistanceStrict(
            typeof date === 'number' ? fromUnixTime(date) : parseISO(date),
            new Date(),
            {
              locale,
              addSuffix: false,
            },
          )
        : '';
    },
  );

  engine.registerFilter('markdown', async text => {
    if (typeof text !== 'string') {
      return '';
    }

    let processedText = text.replace(/\n{3,}/g, '\n\n');

    const { Remarkable } = await remarkableP;

    // Use the processedText for rendering
    processedText = new Remarkable({ breaks: true, html: true }).render(
      processedText,
    );

    return processedText;
  });

  engine.registerFilter(
    'google_map_coordinates',
    (coordinates, ...parameterEntries) => {
      const style = parameterEntries
        .map(([key, value]) => `${key}:${value}`)
        .join('|');
      return coordinates
        .map((path: any) => {
          const points = path
            .flat()
            .map(([lng, lat]: [number, number]) => `${lat},${lng}`)
            .join('|');
          return `&path=${style}|${points}`;
        })
        .join('');
    },
  );

  engine.registerFilter('google_map_markers', markers => {
    return markers
      .map(({ lat, lng, ...params }: any) => {
        const style = Object.entries(params)
          .map(
            ([key, value]) =>
              `${key}:${typeof value === 'string' ? value : ''}`,
          )
          .join('|');
        return `&markers=${style}|${lat},${lng}`;
      })
      .join('');
  });

  engine.registerFilter(
    'google_map_circle_paths',
    (circles, ...parameterEntries) => {
      const { zoom } = Object.fromEntries(parameterEntries);

      // Hide circles at zoom levels 10-13 (inclusive) - circles are too small at those zoom levels
      if (zoom !== undefined && zoom >= 10 && zoom <= 13) {
        return '';
      }

      if (!circles || !Array.isArray(circles) || circles.length === 0) {
        return '';
      }

      return circles
        .map((circle: { lat: number; lng: number; radius: number }) => {
          const { lat, lng, radius } = circle;

          // Generate 32 points forming a circle, then close the polygon
          const points: { lat: number; lng: number }[] = [];
          const numPoints = 32;
          const metersPerDegreeLat = 111320;
          const cosLat = Math.cos((lat * Math.PI) / 180);
          // Guard against polar latitudes where cosLat approaches zero
          const metersPerDegreeLng =
            cosLat > 0.01
              ? metersPerDegreeLat * cosLat
              : metersPerDegreeLat * 0.01;

          for (let i = 0; i < numPoints; i++) {
            const angle = (i / numPoints) * 2 * Math.PI;
            const latOffsetMeters = radius * Math.cos(angle);
            const lngOffsetMeters = radius * Math.sin(angle);
            const latOffset = latOffsetMeters / metersPerDegreeLat;
            const lngOffset = lngOffsetMeters / metersPerDegreeLng;
            points.push({
              lat: lat + latOffset,
              lng: lng + lngOffset,
            });
          }

          // Close the polygon by adding the first point again
          points.push(points[0]!);

          // Format points as lat,lng|lat,lng|...
          const pathCoordinates = points
            .map(point => `${point.lat},${point.lng}`)
            .join('|');

          // Return formatted path string with fill and stroke colors
          return `&path=fillcolor:0x66666633|color:0x333333FF|weight:2|${pathCoordinates}`;
        })
        .join('');
    },
  );

  engine.registerFilter(
    'football_chart',
    memoFilter(
      'football_chart',
      async (appraisal: any, ...parameterEntries) => {
        const {
          height,
          width,
          comparables,
          livingSurface,
          builtSurface,
          suggestedValue,
          isPriceRange,
          priceRangeMin,
          priceRangeMax,
          caprateValue,
          intrinsicValue,
          currency,
          includeCuprate,
          includeIntrinsic,
          includeHedonisticValuation,
          includeComparablesValuation,
          transactionStats,
          locale,
          tenantSettings,
          isPropertyComparableListingsSquareMeterCalculation,
          comparableStats,
        } = Object.fromEntries(parameterEntries);

        const margins = {
          left: 130,
          top: 20,
          right: 30,
          bottom: 30,
        };

        const { scaleLinear, scalePoint } = await d3ScaleP;
        const { extent, median } = await d3ArrayP;

        // When isPropertyComparableListingsSquareMeterCalculation is ON: use price per sqm calculation
        // When OFF: use actual sale prices from comparableStats
        let comparableMedianValue: number | undefined;
        let comparableLineValues: [number, number] | undefined;

        if (isPropertyComparableListingsSquareMeterCalculation) {
          // Use price per sqm * property surface
          const isSpain = tenantSettings?.countryCode === 'ES';
          const pricePerSqmKey = isSpain
            ? 'computedPricePerSqm'
            : 'salePricePerLivingSurface';
          const surface = isSpain ? builtSurface : livingSurface;
          const filteredComparables = comparables.filter(
            (comparable: any) => comparable[pricePerSqmKey],
          );
          const comparableValues: number[] | undefined =
            filteredComparables.length > 0
              ? filteredComparables.map(
                  (comparable: any) => comparable[pricePerSqmKey] * surface,
                )
              : undefined;
          comparableLineValues = comparableValues
            ? (extent(comparableValues) as [number, number])
            : undefined;
          comparableMedianValue = comparableValues
            ? median(comparableValues)
            : undefined;
        } else {
          // Use actual sale prices from comparableStats
          comparableMedianValue = comparableStats?.medianPrice ?? undefined;
          comparableLineValues =
            comparableStats?.lowestPrice && comparableStats?.highestPrice
              ? [comparableStats.lowestPrice, comparableStats.highestPrice]
              : undefined;
        }

        const hedonistLineValues: [number, number] = [
          appraisal?.realadvisor.min,
          appraisal?.realadvisor.max,
        ];

        type ValueType = {
          name: string;
          value?: number;
          lineValue?: [number, number];
          hide?: boolean;
        };

        const translations = {
          hedonist: {
            en: 'AVM',
            es: 'AVM',
            it: 'AVM',
            de: 'AVM',
            fr: 'AVM',
            pl: 'AVM',
            pt: 'AVM',
          },
          comparables: {
            en: 'Listings',
            es: 'Anuncios',
            it: 'Annunci',
            de: 'Anzeigen',
            fr: 'Annonces',
            pl: 'Ogłoszenia',
            pt: 'Anúncios',
          },
          caprate: {
            en: 'Cap-rate',
            es: 'Tasa de retorno',
            it: 'Cap-rate',
            de: 'Kapitalisierungsrate',
            fr: 'Taux de capitalisation',
            pl: 'Stopa kapitalizacji',
            pt: 'Taxa de capitalização',
          },
          intrinsic: {
            en: 'Intrinsic',
            es: 'Intrínseco',
            it: 'Intrinseca',
            de: 'Intrinsisch',
            fr: 'Intrinsèque',
            pl: 'Wartość wewnętrzna',
            pt: 'Intrínseco',
          },
          transactions: {
            en: 'Transactions',
            es: 'Transacciones',
            it: 'Transazioni',
            de: 'Transaktionen',
            fr: 'Transactions',
            pl: 'Transakcje',
            pt: 'Transações',
          },
        };

        const values: ValueType[] = [
          {
            name: translations.hedonist[locale as Language],
            value: median(hedonistLineValues),
            lineValue: hedonistLineValues,
            hide: !includeHedonisticValuation,
          },
          {
            name: translations.comparables[locale as Language],
            value: comparableMedianValue,
            lineValue: comparableLineValues,
            hide: !includeComparablesValuation,
          },
          {
            name: translations.caprate[locale as Language],
            value: caprateValue,
            hide: !includeCuprate,
          },
          {
            name: translations.intrinsic[locale as Language],
            value: intrinsicValue,
            hide: !includeIntrinsic,
          },
          {
            name: translations.transactions[locale as Language],
            value: transactionStats?.medianPrice,
            lineValue:
              transactionStats &&
              transactionStats.lowestPrice &&
              transactionStats.highestPrice
                ? ([
                    transactionStats.lowestPrice,
                    transactionStats.highestPrice,
                  ] as [number, number])
                : undefined,
            hide: !transactionStats || !transactionStats.medianPrice,
          },
        ].filter(e => !e.hide && e.value);

        const pricesBounds = extent(
          [
            ...hedonistLineValues,
            ...(comparableLineValues ?? []),
            caprateValue,
            intrinsicValue,
            suggestedValue,
            ...(isPriceRange ? [priceRangeMin, priceRangeMax] : []),
            ...(transactionStats
              ? [
                  transactionStats.lowestPrice,
                  transactionStats.highestPrice,
                  transactionStats.medianPrice,
                ]
              : []),
          ].filter(price => price),
        );

        const additionalMargin = 90;
        const xScale = scaleLinear()
          .domain([pricesBounds[0], pricesBounds[1]])
          //Adding an additional margins in order to let the text fit the chart
          .range([
            margins.left + additionalMargin,
            width - margins.right - additionalMargin,
          ]);

        // Don't trusct xScale.tick(), since in some cases it will give not correct amount of ticks
        // Here we manually create ticks for xAxis
        const tickCount = 5;
        const xAxis = [...Array(tickCount).keys()].map(
          k =>
            ((pricesBounds[1] - pricesBounds[0]) * k) / (tickCount - 1) +
            pricesBounds[0],
        );

        const yScale = scalePoint()
          .domain(values.map(v => v.name))
          .range([margins.top, height - margins.bottom])
          .padding(0.5);

        const XAxisComponent = `<g transform="translate(0,${
          height - margins.bottom
        })">
        <path d="M${margins.left + 0.5},6V0.5H${
          width - margins.right + 0.5
        }V6" stroke="#000" stroke-width="1" fill="none" />
        ${xAxis
          .map(
            (tick: number) =>
              `
              <g opacity="1" transform="translate(${
                xScale(tick) + 0.5
              },0)" text-anchor="middle" font-size="10">
                <line stroke="currentColor" y2="6" />
                <text y="9" dy="0.71em" style="font-weight: 600;">
                  ${tick.toLocaleString(undefined, {
                    style: 'currency',
                    currency,
                    maximumSignificantDigits: 2,
                    notation: 'compact',
                  })}
                </text>
              </g>
            `,
          )
          .join('')}
      </g>`;

        const YAxisComponent = `<g transform="translate(${
          margins.left
        },0)" fill="none" font-size="10" text-anchor="end">
        <path d="M0.5,20.5V350.5" stroke="#000" />
        ${values
          .map(
            ({ name }) => `
          <g opacity="1" transform="translate(0,${yScale(name)! + 0.5})">
            <text fill="currentColor" x="-5" dy="0.32em" style="font-weight: 600;"> ${name} </text>
          </g>
        `,
          )
          .join('')}
      </g>`;

        const SuggestedValueLine = suggestedValue
          ? `<line x1="${xScale(suggestedValue)}" x2="${xScale(
              suggestedValue,
            )}" y1="${margins.top}" y2="${
              height - margins.bottom
            }" stroke-dasharray="3" stroke-width="2" stroke="#49945A" />`
          : '';

        const PriceRangeLines =
          isPriceRange && priceRangeMin && priceRangeMax
            ? `
          <line x1="${xScale(priceRangeMin)}" x2="${xScale(
                priceRangeMin,
              )}" y1="${margins.top}" y2="${
                height - margins.bottom
              }" stroke-dasharray="3" stroke-width="2" stroke="#49945A" />
          <line x1="${xScale(priceRangeMax)}" x2="${xScale(
                priceRangeMax,
              )}" y1="${margins.top}" y2="${
                height - margins.bottom
              }" stroke-dasharray="3" stroke-width="2" stroke="#FF0000" />
          <line x1="${xScale(
            (priceRangeMin + priceRangeMax) / 2,
          )}" x2="${xScale((priceRangeMin + priceRangeMax) / 2)}" y1="${
                margins.top
              }" y2="${
                height - margins.bottom
              }" stroke-dasharray="3" stroke-width="2" stroke="#000000" />`
            : '';

        const gradients = values
          .filter(value => value.lineValue)
          .map(
            ({ name, lineValue }) => `
      <linearGradient id="gradient${name}" x1="${xScale(
              lineValue![0],
            )}" y1="${yScale(name)}" x2="${xScale(lineValue![1])}" y2="${yScale(
              name,
            )}" gradientUnits="userSpaceOnUse">
          <stop class="start" offset="0%" stop-color="#55BAD9" stop-opacity="1" />
          <stop class="end" offset="100%" stop-color="#203197" stop-opacity="1" />
      </linearGradient>
    `,
          )
          .join('');

        const renderLineAndDots = ({ lineValue, name, value }: ValueType) =>
          lineValue
            ? `
          <line stroke-width="8" stroke="url(#gradient${name})" stroke-linecap='round' x1="${xScale(
                lineValue[0],
              )}" y1="${yScale(name)}" x2="${xScale(
                lineValue[1],
              )}" y2="${yScale(name)}" />

            ${renderDots({ value, name })}


            <rect x="${xScale(lineValue[0]) - 5}" y="${
                (yScale(name) as number) - 8
              }" width="80" height="15" fill="#FFF" transform="translate(-80, 0)"/>
            <text x="${xScale(lineValue[0]) - 5}" y="${
                (yScale(name) as number) + 4
              }" text-anchor="end">
                ${lineValue[0].toLocaleString(undefined, {
                  style: 'currency',
                  currency,
                  maximumFractionDigits: 0,
                })}
            </text>

            <rect x="${xScale(lineValue[1]) + 5}" y="${
                (yScale(name) as number) - 8
              }" width="80" height="15" fill="#FFF"/>

            <text x="${xScale(lineValue[1]) + 5}" y="${
                (yScale(name) as number) + 4
              }" text-anchor="start">
                ${lineValue[1].toLocaleString(undefined, {
                  style: 'currency',
                  currency,
                  maximumFractionDigits: 0,
                })}
              </text>`
            : `${renderDots({ value, name })}`;

        const renderDots = ({ value, name }: ValueType) =>
          value
            ? `
            <circle cx="${xScale(value)}" cy="${yScale(
                name,
              )}" r="5" fill="#FFF" stroke-width="2" stroke="#3b59c3" />

            <rect x="${xScale(value) - 50}" y="${
                (yScale(name) as number) - 22
              }" width="100" height="15" fill="#FFF"/>

            <text x="${xScale(value)}" y="${
                (yScale(name) as number) - 10
              }" style="font-weight: 600;" text-anchor="middle">
            ${value.toLocaleString(undefined, {
              style: 'currency',
              currency,
              maximumFractionDigits: 0,
            })}
            </text>`
            : '';

        const LinesAndDots = values
          .map(value => renderLineAndDots(value))
          .join('');

        return `<svg width="${width}" height="${height}">
        <defs>
          ${gradients}
        </defs>
        ${XAxisComponent}
        ${YAxisComponent}
        ${!isPriceRange ? SuggestedValueLine : PriceRangeLines}
        ${LinesAndDots}
      </svg>`;
      },
    ),
  );

  // hey! if you're making changes to this filter, please make sure to check if
  // you need to update realadvisor/realadvisor.crm/src/shared/appraisal-graph.js
  // also! thanks! and have a great day! 🌞

  engine.registerFilter(
    'appraisal_graph',
    memoFilter(
      'appraisal_graph',
      async (appraisal: any, ...parameterEntries) => {
        const { scaleLinear, scalePoint } = await d3ScaleP;

        if (appraisal == null) {
          return '';
        }

        const { tenantSettings, height, width, locale } =
          Object.fromEntries(parameterEntries);

        const getAppraisalDomain = (appraisal: any) => {
          const getTuple = (item: any) => [item?.min, item?.value, item?.max];

          const values = [
            ...getTuple(appraisal.iazi),
            ...getTuple(appraisal.iaziCv),
            ...getTuple(appraisal.wup),
            ...getTuple(appraisal.pricehubble),
            ...getTuple(appraisal.realadvisorListing),
            ...getTuple(appraisal.realadvisorNaiveListings),
            ...getTuple(appraisal.realadvisorOmniMeta),
            ...getTuple(appraisal.realadvisorTransaction),
            ...getTuple(appraisal.realadvisorOpenDataTransaction),
            ...getTuple(appraisal.realadvisorPerceived),
            ...getTuple(appraisal.realadvisor),
          ].filter(d => d != null);

          return [Math.min(...values), Math.max(...values)];
        };

        // also see realadvisor/realadvisor.crm/src/shared/appraisal-graph.js
        const formatValue = (value: number) => {
          if (value < 1_000) {
            return value.toLocaleString(locale ?? mainLocale, {
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            });
          }
          if (value < 1_000_000) {
            return (
              (value / 1_000).toLocaleString(locale ?? mainLocale, {
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
              }) + 'K'
            );
          }
          return (
            (value / 1_000_000).toLocaleString(locale ?? mainLocale, {
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            }) + 'M'
          );
        };

        // collect points data and remove items without values
        const appraisalData = getAppraisalData(
          tenantSettings,
          appraisal,
        ).filter(
          item => item.min != null || item.value != null || item.max != null,
        );
        const x = scalePoint()
          .domain(appraisalData.map(item => item.name))
          .range([0, width])
          .padding(1);
        const y = scaleLinear()
          .domain(getAppraisalDomain(appraisal))
          .range([height - 30, 20])
          .nice(5);
        const ticks = y.ticks(5);
        const xStart = x(appraisalData[0]?.name ?? '');
        const xEnd = x(appraisalData[appraisalData.length - 1]?.name ?? '');

        return `
      <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
        <g transform="translate(0, ${height - 30})">
          ${appraisalData
            .map(item => {
              const pos = x(item.name);
              return `
                <line x1="${pos}" x2="${pos}" y1="0" y2="6" stroke="rgba(102, 102, 102, 1)" />
                <text
                  x="${pos}"
                  y="16"
                  dy="0.71em"
                  text-anchor="middle"
                  font-size="12px"
                  font-weight="bold"
                >
                  ${item.name}
                </text>
              `;
            })
            .join('')}
        </g>

        <g transform="translate(34, 0)">
          ${ticks
            .map(
              tick => `
                <line
                  x1="15"
                  x2="${width}"
                  y1="${y(tick)}"
                  y2="${y(tick)}"
                  fill="none"
                  stroke="rgba(0, 0, 0, .12)"
                  stroke-width="2"
                  stroke-dasharray="3 6"
                />
                <text
                  x="8"
                  y="${y(tick)}"
                  text-anchor="end"
                  font-size="10px"
                  font-weight="bold"
                >
                  ${formatValue(tick)}
                </text>
              `,
            )
            .join('')}
        </g>
        <line
          x1="${xStart}"
          x2="${xEnd}"
          y1="${y(appraisal.realadvisor?.max)}"
          y2="${y(appraisal.realadvisor?.max)}"
          fill="none"
          stroke="rgba(76, 175, 80, 1)"
          stroke-width="1"
          stroke-dasharray="2 2"
        />

        <line
          x1="${xStart}"
          x2="${xEnd}"
          y1="${y(appraisal.realadvisor?.value)}"
          y2="${y(appraisal.realadvisor?.value)}"
          fill="none"
          stroke="rgba(76, 175, 80, 1)"
          stroke-width="2"
          stroke-dasharray="2 2"
        />

        <line
          x1="${xStart}"
          x2="${xEnd}"
          y1="${y(appraisal.realadvisor?.min)}"
          y2="${y(appraisal.realadvisor?.min)}"
          fill="none"
          stroke="rgba(76, 175, 80, 1)"
          stroke-width="1"
          stroke-dasharray="2 2"
        />

        ${appraisalData
          .filter(item => item.max != null)
          .map(
            item => `
              <circle
                cx="${x(item.name)}"
                cy="${y(item.max)}"
                r="3"
                fill="white"
                stroke="rgba(100, 181, 246, 1)"
                stroke-width="2"
              />
              <text
                x="${x(item.name)}"
                dx="20"
                y="${y(item.max)}"
                font-size="10px"
                font-weight="normal"
                text-anchor="middle"
              >
                ${formatValue(item.max)}
              </text>
            `,
          )
          .join('')}

        ${appraisalData
          .filter(item => item.value != null)
          .map(
            item => `
              <circle
               cx="${x(item.name)}"
               cy="${y(item.value)}"
               r="5"
               fill="rgba(100, 181, 246, 1)"
              />
              <text
                x="${x(item.name)}"
                dx="20"
                y="${y(item.value)}"
                font-size="12px"
                font-weight="bold"
                text-anchor="middle"
              >
                ${formatValue(item.value)}
              </text>
            `,
          )
          .join('')}

        ${appraisalData
          .filter(item => item.min != null)
          .map(
            item => `
              <circle
                cx="${x(item.name)}"
                cy="${y(item.min)}"
                r="3"
                fill="white"
                stroke="rgba(100, 181, 246, 1)"
                stroke-width="2"
              />
              <text
                x="${x(item.name)}"
                dx="20"
                y="${y(item.min)}"
                font-size="10px"
                font-weight="normal"
                text-anchor="middle"
              >
                ${formatValue(item.min)}
              </text>
            `,
          )
          .join('')}
      </svg>
    `;
      },
    ),
  );

  engine.registerFilter(
    'market_data_graph',
    memoFilter('market_data_graph', async (marketData, ...params) => {
      if (!marketData || !Array.isArray(marketData)) return '';
      const { width = 690, height = 280, colors } = Object.fromEntries(params);

      const m = { top: 20, right: 20, bottom: 40, left: 40 };
      const w = width - m.left - m.right;
      const h = height - m.top - m.bottom;

      const { scaleLinear, scaleTime } = await d3ScaleP;

      if (!marketData[0]?.historicalData?.length) {
        let svg = `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`;
        svg += `<rect width="${width}" height="${height}" fill="white"/>`;
        svg += '<g transform="translate(' + m.left + ',' + m.top + ')">';
        svg += '<text x="20" y="20">No data available</text>';
        svg += '</g></svg>';
        return svg;
      }

      let svg = `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`;
      svg += `<rect width="${width}" height="${height}" fill="white"/>`;

      // Main graph content
      svg += `<g transform="translate(${m.left},${m.top})">`;

      // Get min/max for Y scale
      let minY = 0;
      let maxY = 0;
      marketData.forEach(p => {
        if (p.historicalData) {
          p.historicalData.forEach((d: { priceEvolution?: number }) => {
            if (typeof d.priceEvolution === 'number') {
              minY = Math.min(minY, d.priceEvolution);
              maxY = Math.max(maxY, d.priceEvolution);
            }
          });
        }
      });

      // Calculate step size like in price-evolution-chart.svelte
      const range = maxY - minY;
      let stepSize = 0.5;
      if (range > 500) stepSize = 100;
      else if (range > 200) stepSize = 50;
      else if (range > 75) stepSize = 25;
      else if (range > 30) stepSize = 10;
      else if (range > 15) stepSize = 5;
      else if (range > 3) stepSize = 1;
      else if (range > 1.5) stepSize = 0.5;
      else stepSize = 0.25;

      // Round to step size
      minY = minY < 0 ? Math.floor(minY / stepSize) * stepSize : 0;
      maxY = maxY > 0 ? Math.ceil(maxY / stepSize) * stepSize : 0;

      const yScale = scaleLinear().domain([minY, maxY]).range([h, 0]);

      // Get date range
      const firstPlace = marketData[0];
      const dates = firstPlace.historicalData
        .map((d: { date: string }) => new Date(d.date))
        .sort((a: Date, b: Date) => a.getTime() - b.getTime());

      const xScale = scaleTime()
        .domain([dates[0], dates[dates.length - 1]])
        .range([0, w]);

      // Generate Y ticks using the step size
      const yTicks: number[] = [];
      for (let i = minY; i <= maxY; i += stepSize) {
        yTicks.push(Math.round(i * 10) / 10);
      }

      // Grid lines
      yTicks.forEach(tick => {
        const y = yScale(tick);
        svg += `<line x1="0" y1="${y}" x2="${w}" y2="${y}" stroke="#e0e0e0" stroke-dasharray="2,2"/>`;
      });

      // Axes
      svg += `<line x1="0" y1="0" x2="0" y2="${h}" stroke="#333"/>`;
      svg += `<line x1="0" y1="${h}" x2="${w}" y2="${h}" stroke="#333"/>`;

      // Y-axis labels - format like Svelte component
      const formatPercentageChange = (change: number) =>
        change
          ? `${change >= 0 ? '' : '('}${Math.round(change * 10) / 10}%${
              change >= 0 ? '' : ')'
            }`
          : '0';

      yTicks.forEach(tick => {
        const y = yScale(tick);
        const label = formatPercentageChange(tick);
        svg += `<text x="-10" y="${y}" text-anchor="end" font-size="12" fill="#666" dominant-baseline="middle">${label}</text>`;
      });

      // X-axis labels (years) - dynamic increment based on year range
      const startYear = dates[0].getFullYear();
      const endYear = dates[dates.length - 1].getFullYear();
      const totalYears = endYear - startYear + 1;

      // Determine optimal increment based on total years
      let yearIncrement = 1; // Default: show every year
      if (totalYears > 15) {
        yearIncrement = 3; // For 16+ years: show every 3rd year
      } else if (totalYears > 8) {
        yearIncrement = 2; // For 9+ years: show every other year
      }

      for (let year = startYear; year <= endYear; year += yearIncrement) {
        const x = xScale(new Date(year, 0, 1));

        // Only show year labels that are within the visible chart area
        if (x >= 0 && x <= w) {
          svg += `<text x="${x}" y="${
            h + 20
          }" text-anchor="middle" font-size="12" fill="#666">${year}</text>`;
        }
      }

      // Draw lines for each place - build all lines first
      let allLines = '';
      for (let i = 0; i < marketData.length; i++) {
        const place = marketData[i];
        if (!place.historicalData || place.historicalData.length < 2) continue;

        const data = place.historicalData
          .filter(
            (d: { date?: string; priceEvolution?: number }) =>
              d.date && typeof d.priceEvolution === 'number',
          )
          .sort(
            (a: { date: string }, b: { date: string }) =>
              new Date(a.date).getTime() - new Date(b.date).getTime(),
          );

        // Draw line segments
        for (let j = 0; j < data.length - 1; j++) {
          const x1 = xScale(new Date(data[j].date));
          const y1 = yScale(data[j].priceEvolution);
          const x2 = xScale(new Date(data[j + 1].date));
          const y2 = yScale(data[j + 1].priceEvolution);

          allLines +=
            '<line x1="' +
            x1 +
            '" y1="' +
            y1 +
            '" x2="' +
            x2 +
            '" y2="' +
            y2 +
            '" stroke="' +
            colors[i] +
            '" stroke-width="2.5"/>\n';
        }
      }
      svg += allLines;

      svg += '</g>';
      svg += '</svg>';

      return svg;
    }),
  );

  // Generate color for market data legend - uses same logic as market_data_graph
  // Heat map filter for generating SVG heat maps
  engine.registerFilter(
    'heat_map_svg',
    memoFilter('heat_map_svg', async (heatMapData, ...params) => {
      if (!heatMapData || !heatMapData.localities || !heatMapData.colorRanges) {
        return '';
      }

      const { width = 600, height = 400 } = Object.fromEntries(params);

      // Since we don't have polygon data, we'll create a simplified grid-based heat map
      let svg = `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">`;
      svg += `<rect width="${width}" height="${height}" fill="#f0f0f0"/>`;

      // Add title
      svg += `<text x="${
        width / 2
      }" y="20" text-anchor="middle" font-size="16" font-weight="bold">Price Heat Map</text>`;

      // Create a simple grid representation
      const gridSize = Math.ceil(Math.sqrt(heatMapData.localities.length));
      const cellWidth = (width - 40) / gridSize;
      const cellHeight = (height - 100) / gridSize;
      const startX = 20;
      const startY = 40;

      // Draw localities as colored rectangles
      heatMapData.localities.forEach(
        (locality: { color?: string; name: string }, index: number) => {
          const row = Math.floor(index / gridSize);
          const col = index % gridSize;
          const x = startX + col * cellWidth;
          const y = startY + row * cellHeight;

          svg += `<rect x="${x}" y="${y}" width="${cellWidth - 2}" height="${
            cellHeight - 2
          }" fill="${
            locality.color ?? '#ccc'
          }" stroke="#fff" stroke-width="1" opacity="0.8"/>`;

          // Add locality name if space permits
          if (cellWidth > 60 && cellHeight > 30) {
            svg += `<text x="${x + cellWidth / 2}" y="${
              y + cellHeight / 2
            }" text-anchor="middle" font-size="10" fill="#333">${locality.name.substring(
              0,
              10,
            )}</text>`;
          }
        },
      );

      // Add color legend at bottom
      const legendY = height - 60;
      const legendHeight = 20;
      const colorWidth = (width - 120) / heatMapData.colorRanges.length;

      svg += `<text x="${width / 2}" y="${
        legendY - 10
      }" text-anchor="middle" font-size="12" font-weight="bold">Price per m² (CHF)</text>`;

      heatMapData.colorRanges.forEach(
        (
          range: {
            color: string;
            minInclusive?: number;
            maxExclusive?: number;
          },
          index: number,
        ) => {
          const x = 60 + index * colorWidth;

          // Color rectangle
          svg += `<rect x="${x}" y="${legendY}" width="${colorWidth}" height="${legendHeight}" fill="${range.color}" stroke="#333" stroke-width="0.5"/>`;

          // Range label
          if (index === 0 && range.maxExclusive) {
            svg += `<text x="${x}" y="${
              legendY + legendHeight + 15
            }" text-anchor="start" font-size="10">&lt; ${Math.round(
              range.maxExclusive,
            )}</text>`;
          } else if (
            index === heatMapData.colorRanges.length - 1 &&
            range.minInclusive
          ) {
            svg += `<text x="${x + colorWidth}" y="${
              legendY + legendHeight + 15
            }" text-anchor="end" font-size="10">&gt; ${Math.round(
              range.minInclusive,
            )}</text>`;
          }
        },
      );

      svg += '</svg>';
      return svg;
    }),
  );

  return {
    render: async ({ content, data, meta }) => {
      const result = await engine.parseAndRender(content, {
        data,
        meta,
      });

      const sanitizedHtml = sanitize(result);

      let finalHtml = sanitizedHtml;

      if (sanitizedHtml.includes('market-map')) {
        finalHtml = sanitizedHtml.replace(
          /<body[^>]*>/i,
          `$&<link href="https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_VERSION}/mapbox-gl.css" rel="stylesheet">`,
        );

        finalHtml = finalHtml.replace(
          '</body>',
          `<script src="https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_VERSION}/mapbox-gl.js"></script>
          <script>
            function initMap() {
              try {
                var config = ${JSON.stringify(data.heatMapConfig || null)};
                if (!config) return;

                var container = document.getElementById('market-map');
                if (!container || container.dataset.mapInit) return;
                container.dataset.mapInit = '1';

                if (config.mapboxToken) {
                  mapboxgl.accessToken = config.mapboxToken;
                  var map = new mapboxgl.Map({
                    container: 'market-map',
                    style: config.style,
                    center: config.center,
                    zoom: config.zoom,
                    interactive: false,
                    attributionControl: false,
                    preserveDrawingBuffer: true
                  });

                  map.on('load', function() {
                    var style = map.getStyle();
                    if (style && style.layers) {
                      style.layers.forEach(function(layer) {
                        if (layer.id.indexOf('admin') >= 0 || layer.id.indexOf('boundary') >= 0 || layer.id.indexOf('poi') >= 0 || layer.id.indexOf('shield') >= 0) {
                          map.setLayoutProperty(layer.id, 'visibility', 'none');
                        }
                      });
                    }

                    new mapboxgl.Marker({ color: '#2563eb' })
                      .setLngLat(config.center)
                      .addTo(map);

                    if (config.sources) {
                      config.sources.forEach(function(source) {
                        if (source.id && !map.getSource(source.id)) {
                          var sourceDef = { type: source.type };
                          if (source.url) sourceDef.url = source.url;
                          if (source.data) sourceDef.data = source.data;
                          map.addSource(source.id, sourceDef);
                        }
                      });
                    }

                    if (config.layers) {
                      config.layers.forEach(function(layer) {
                        map.addLayer(layer);
                      });
                    }
                  });

                  map.on('error', function(e) {
                    console.error('Mapbox error:', e);
                    window.status = 'ready';
                  });
                }
              } catch (e) {
                console.error('Map init error:', e);
              }
            }
            window.addEventListener('pagedjs:rendering:complete', initMap);
          </script>
          </body>`,
        );
      }

      finalHtml = finalHtml.replace(
        '</body>',
        `
        <script src="https://cdn.tailwindcss.com/3.4.1"></script>
        <script>${PAGED_RENDERER_SCRIPT}</script>
        </body>
        `,
      );

      return finalHtml;
    },
  };
}
